import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { CoreModule } from '@core/core.module';
import {
	DocumentRequest,
	DocumentRequestDocument,
	DOCUMENT_REQUEST_STATUS,
} from '@models/document-requests';
import { select, Store } from '@ngxs/store';
import {
	AdminUser,
	AdminUsersService,
} from '@repos/admin-users/admin-users.service';
import { DocumentRequestsRepo } from '@repos/document-requests/document-requests.repo';
import * as DocumentRequestsActions from '@state/document-requests/document-requests.actions';
import { DocumentRequestsSelectors } from '@state/document-requests/document-requests.selectors';
import {
	Observable,
	firstValueFrom,
	from,
	lastValueFrom,
	map,
	of,
	switchMap,
	take,
} from 'rxjs';
import { DRAWER_DATA } from 'src/app/ui/drawer/drawer-data.token';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { ModalService } from 'src/app/ui/modal/modal.service';
import { RequestDocumentPreviewComponent } from '../request-document-preview/request-document-preview.component';
import { ToastService } from 'src/app/ui/toast/toast.service';
import { UserSelectors } from '@state/global/user';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
interface DocumentRequestWithAdminUser extends DocumentRequest {
	assignedToUser: AdminUser;
	documents: DocumentRequestDocument[];
}

@Component({
	selector: 'app-request-details',
	templateUrl: './request-details.component.html',
	imports: [CommonModule, CoreModule, IconsModule],
})
export class RequestDetailsComponent {
	store = inject(Store);
	drawerService = inject(DrawerService);
	firestore = inject(Firestore);
	storage = inject(AngularFireStorage);
	requestId = inject(DRAWER_DATA).requestId;
	modalService = inject(ModalService);
	toastService = inject(ToastService);
	canMakeRequests = select(UserSelectors.canUserMakeRequests);
	details$: Observable<DocumentRequestWithAdminUser | DocumentRequest | null>;
	DocumentRequestStatus = DOCUMENT_REQUEST_STATUS;

	constructor(
		private adminUsersService: AdminUsersService,
		private documentRequestsRepo: DocumentRequestsRepo,
	) {
		this.details$ = this.store
			.select(DocumentRequestsSelectors.getDocumentRequestById)
			.pipe(
				switchMap((getById) => {
					const request = getById(this.requestId);
					if (!request) return of(null);

					return from(
						this.documentRequestsRepo.getDocumentRequestDocuments(
							this.requestId,
						),
					).pipe(
						map((documents) => ({
							...request,
							documents,
						})),
						switchMap((requestWithDocs) => {
							if (!requestWithDocs.assignedTo) return of(requestWithDocs);

							return this.adminUsersService
								.Get$(requestWithDocs.assignedTo)
								.pipe(
									map((adminUser) => ({
										...requestWithDocs,
										assignedToUser: adminUser,
									})),
								);
						}),
					);
				}),
			);
	}

	async cancelRequest() {
		const request = await firstValueFrom(this.details$.pipe(take(1)));
		if (!request) return;

		await this.store.dispatch(
			new DocumentRequestsActions.Update({
				...request,
				status: DOCUMENT_REQUEST_STATUS.CANCELED,
			}),
		);
		this.toastService.success('Request canceled successfully');
	}

	async previewDocument(doc: DocumentRequestDocument) {
		try {
			const fileRef = this.storage.ref(doc.storagePath);
			const downloadUrl = await lastValueFrom(
				fileRef.getDownloadURL().pipe(take(1)),
			);

			this.drawerService.openDrawer(RequestDocumentPreviewComponent, {
				title: doc.name,
				data: {
					downloadUrl,
				},
				noStyle: true,
				size: 'lg',
			});
		} catch (error) {
			console.error('Error getting download URL:', error);
		}
	}
}
